import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useParams,
} from 'react-router-dom';
import type { ReactNode } from 'react';
import { AuthProvider, useAuth } from './AuthProvider.tsx';
import { SignIn } from '../features/account/SignIn.tsx';
import { AccountPage } from '../features/account/AccountPage.tsx';
import { PracticeShell } from '../features/practice/PracticeShell.tsx';
import { AppLayout } from './AppLayout.tsx';
import { LessonsPage } from '../features/content/LessonsPage.tsx';
import { LessonReview } from '../features/content/LessonReview.tsx';
import { RankingPage } from '../features/ranking/RankingPage.tsx';
import { ProfilePage } from '../features/profile/ProfilePage.tsx';
import { LessonList } from '../features/admin/LessonList.tsx';
import { UsersList } from '../features/admin/UsersList.tsx';
import { ActivityList } from '../features/admin/ActivityList.tsx';
import { AdminAlerts } from '../features/admin/AdminAlerts.tsx';
import { AdminOverview } from '../features/admin/AdminOverview.tsx';
import { AdminAreaPlaceholder } from '../features/admin/AdminAreaPlaceholder.tsx';
import { LoadingState } from '../ui/components/LoadingState/LoadingState.tsx';
import { useDelayedLoading } from '../ui/components/LoadingState/useDelayedLoading.ts';
import { OfflineState } from '../ui/components/OfflineState/OfflineState.tsx';
import { Surface } from '../ui/primitives/Surface.tsx';
import { Text } from '../ui/primitives/Text.tsx';

/**
 * The route map and the two authorization boundaries.
 *
 * This file establishes *where* a learner or an administrator may go. Every
 * route carries its real screen.
 *
 * NO ROUTE CARRIES THE LEARNER'S OWN POSITION. `/praticar` opens the Exercise
 * the cursor stands on, `/` draws the spine with the Lesson the cursor is in
 * already open, and `/revisao` reports the Lesson just finished — none of them
 * names a Lesson or an Exercise, because a learner has ONE position and the
 * server owns it. An address a learner can read, remember and type is worth
 * more than one that can be forwarded, because the thing it would forward —
 * someone else's place in the book — is not something this product has any use
 * for.
 *
 * `/perfil/:userId` IS THE ONE EXCEPTION, and it is the exception that shows
 * what the rule was about. What it names is not a position in the book, it is
 * ANOTHER PERSON — and a person is precisely the kind of thing worth sending
 * to somebody in a group chat. The moment the product gained a follow graph it
 * gained something addressable, and `/convite/:userId` is the same address
 * under the name a learner would use when sharing it.
 *
 * Route segments are named in Portuguese — `/praticar`, `/conta`, `/entrar` —
 * because a URL is something the learner reads, and this platform speaks
 * Portuguese to them everywhere else. The rule covers the default and
 * redirect targets too, so there is no English route to fall back to. It does
 * not reach the code: components, props and the server's own `/api/*` remain
 * English. `/admin` is the one segment that is already both.
 *
 * Authorization is not enforced here. `apps/api` is authoritative: every
 * learner route is behind SessionGuard (401) and every /api/admin/* route is
 * behind AdminGuard (403, empty body — FR-061, SC-011). These boundaries exist
 * so a signed-out visitor lands on sign-in instead of an empty screen, and so
 * a learner who types /admin is sent back rather than shown a shell that will
 * only ever return 403. Removing them would be a usability regression, not a
 * security one.
 *
 * They are what the development door in `app/dev-auth.ts` satisfies while the
 * platform is being built: choosing a role there is enough to pass both, so
 * every screen can be reached without an account and without weakening either
 * boundary here.
 */

/**
 * Mounted only while a boundary is undecided, which is almost always shorter
 * than the loader's delay — so the usual visit sees nothing here at all, and
 * never an instrument that blinks past on the way to the real screen.
 */
function Resolving({ label, standalone = false }: { label: string; standalone?: boolean }) {
  const show = useDelayedLoading(true);
  return show ? <LoadingState label={label} standalone={standalone} /> : null;
}

/**
 * FR-027 — no Exercise is reachable without a signed-in account.
 *
 * `unavailable` is deliberately not treated as signed out: a learner whose
 * connection dropped mid-Lesson is not sent to sign-in, they are told the
 * application cannot be reached.
 */
function RequireSession({ children }: { children?: ReactNode }) {
  const { state, refresh } = useAuth();
  const location = useLocation();

  if (state.status === 'loading') return <Resolving standalone label="Verificando sua sessão" />;

  if (state.status === 'unavailable') {
    return (
      <OfflineState
        standalone
        title="Não foi possível falar com o servidor"
        onRetry={() => void refresh()}
      />
    );
  }

  if (state.status === 'anonymous') {
    // `from` is carried so sign-in can return the learner to where they were
    // headed rather than to a generic landing route.
    return <Navigate to="/entrar" replace state={{ from: location.pathname }} />;
  }

  return <>{children ?? <Outlet />}</>;
}

/**
 * FR-061 — the administrator experience is reachable only by an
 * operator-provisioned administrator. Nested inside RequireSession, so an
 * unauthenticated visitor is answered by that boundary first.
 */
function RequireAdmin({ children }: { children?: ReactNode }) {
  const { state } = useAuth();

  if (state.status !== 'authenticated') return <Resolving label="Verificando suas permissões" />;
  if (state.user.role !== 'admin') return <Navigate to="/" replace />;

  // The frozen-administrator banner (FR-084) is the route's, not any one
  // screen's, so every admin screen carries it without having to ask.
  return (
    <>
      <AdminAlerts />
      {children ?? <Outlet />}
    </>
  );
}

/**
 * `/convite/:id` and `/perfil/:id` are the same page; this is only the name
 * the link carries. `replace` so that going back from a profile does not walk
 * the visitor through the invite address again.
 */
function InviteRedirect() {
  const { userId } = useParams<{ userId: string }>();
  return <Navigate to={`/perfil/${userId}`} replace />;
}

/** Sends an already-signed-in visitor away from the sign-in route. */
function AnonymousOnly({ children }: { children?: ReactNode }) {
  const { state } = useAuth();

  if (state.status === 'loading') return <Resolving standalone label="Verificando sua sessão" />;
  // The journey, not practice: a learner who has just signed in should land
  // where they can see what they are working through.
  if (state.status === 'authenticated') return <Navigate to="/" replace />;

  return <>{children ?? <Outlet />}</>;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <Outlet />,
    children: [
      {
        path: 'entrar',
        element: <AnonymousOnly />,
        children: [{ index: true, element: <SignIn /> }],
      },
      {
        element: <RequireSession />,
        children: [
          /*
           * Practice sits OUTSIDE `AppLayout`. § 14 gives navigation two
           * patterns: the segmented pill for moving between places, and the
           * task bar for a focused task with one escape. An exercise is a
           * focused task, and putting a "go somewhere else" pill beside it
           * would work against the whole point of the screen.
           */
          { path: 'praticar', element: <PracticeShell /> },

          {
            element: <AppLayout />,
            children: [
              /*
               * EVERY PLACE ANSWERS ONE QUESTION, AND ONLY ONE.
               *
               * `/` is Aprenda — the spine — and asks "where am I in the
               * book?", answered with the learner's position on it and the
               * open Lesson absorbed inline. `/ranking` asks "how is my week
               * going next to theirs?" and `/perfil` asks "was any of this
               * worth it?" — two screens rather than one, because a week that
               * resets and a total that never does are opposite answers.
               * `/conta` is the settings, not the person.
               *
               * The spine IS the landing screen. It is the product, not an
               * antechamber to it: a learner arrives and sees the book, not a
               * single button standing in front of it.
               */
              { index: true, element: <LessonsPage /> },

              /*
               * The one exception to "a Lesson has no screen of its own", and
               * it earns the address by asking a different question. The spine
               * answers "where am I"; this answers "how did that go" — the
               * answers recorded in one Lesson, and the way back into it.
               *
               * IT NAMES NO LESSON, for the same reason `/praticar` names no
               * Exercise: the learner has one position, and the platform is
               * what knows where it is. Opening a finished Lesson's answers
               * hands this route that Lesson in navigation state; arriving
               * cold — a refresh, a pasted link — it reviews the last Lesson
               * the learner finished, which is what "how did that go" means
               * to someone who typed the address rather than pressed
               * something. Either way the screen leads with the Lesson's own
               * title, so it is never ambiguous which one is being read.
               */
              { path: 'revisao', element: <LessonReview /> },

              /*
               * The week, and the years. Two screens rather than one, because
               * they answer opposite questions: `/ranking` asks "how is my
               * week going next to theirs", which resets every Monday so a
               * newcomer can still win one; `/perfil` asks "was any of this
               * worth it", which never resets and never falls.
               */
              { path: 'ranking', element: <RankingPage /> },
              { path: 'perfil', element: <ProfilePage /> },
              { path: 'perfil/:userId', element: <ProfilePage /> },

              /*
               * An invite is not a second kind of page — it is a profile under
               * the name somebody uses when sharing it. A visitor without an
               * account meets `RequireSession` on the way in and is returned
               * here afterwards, exactly like any other address.
               */
              { path: 'convite/:userId', element: <InviteRedirect /> },
              { path: 'conta', element: <AccountPage /> },
              {
                path: 'admin',
                element: <RequireAdmin />,
                children: [
                  { index: true, element: <AdminOverview /> },
                  { path: 'conteudo', element: <LessonList /> },
                  { path: 'usuarios', element: <UsersList /> },
                  { path: 'atividade', element: <ActivityList /> },
                  {
                    path: 'pagamentos',
                    element: (
                      <AdminAreaPlaceholder
                        title="Pagamentos"
                        description="Assinaturas e cobrança dos alunos."
                      />
                    ),
                  },
                  {
                    path: 'comunidade',
                    element: (
                      <AdminAreaPlaceholder
                        title="Comunidade"
                        description="Espaço para os alunos interagirem entre si."
                      />
                    ),
                  },
                  {
                    path: 'eventos',
                    element: (
                      <AdminAreaPlaceholder
                        title="Eventos"
                        description="Aulas ao vivo e encontros da comunidade."
                      />
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '*',
        element: (
          <Surface layer={1} inset>
            <Text as="h1" variant="heading">
              Página não encontrada
            </Text>
          </Surface>
        ),
      },
    ],
  },
]);

/**
 * The application's composition root: the account is resolved once, outside
 * the router, so every boundary reads the same state and a navigation never
 * re-asks who is signed in.
 */
export function AppRouter() {
  return (
    <AuthProvider>
      <RouterProvider router={router} />
    </AuthProvider>
  );
}
